// frontend/src/components/playbook/patterns.ts
//
// The 10 patterns the engine detects (backend/src/services/signals/
// patternDetection.ts), written for a beginner: what the shape says, the
// exact rule the code uses, where it matters, and a diagram. "textbook" is
// the success rate hard-coded in the detector; "measured" comes from the
// pattern study (bun src/backtest/patterns/run.ts) — see MEASURED below.

export type PatternBias = "bullish" | "bearish" | "neutral";

/** Candles in a 0–100 price space (up = higher price): [open, high, low, close]. */
export type Bar = [number, number, number, number];

export type Diagram =
	| {
			type: "candles";
			bars: Bar[];
			/** First and last index of the pattern candles (highlighted). */
			highlight: [number, number];
			label: string;
	  }
	| {
			type: "line";
			/** Price path in the 0–100 space, evenly spaced. */
			points: number[];
			neckline: number;
			/** Index where the close through the neckline confirms the pattern. */
			breakAt: number;
			target: number;
			stop: number;
			marks: Array<{ i: number; label: string }>;
	  };

export interface PatternInfo {
	/** Engine name, also the key into MEASURED. */
	name: string;
	kind: "candle" | "chart";
	bias: PatternBias;
	size: string;
	says: string;
	rules: string[];
	where: string;
	/** Also an entry trigger for the trend-pullback setup. */
	trigger?: boolean;
	/** Textbook success rate hard-coded in the detector. */
	textbook: number;
	diagram: Diagram;
}

export const PATTERNS: PatternInfo[] = [
	{
		name: "Hammer",
		kind: "candle",
		bias: "bullish",
		size: "1 candle",
		says: "Sellers pushed price far down during the hour, but buyers pushed it nearly all the way back before the close. The long tail underneath is the sell-off that failed.",
		rules: ["Lower wick (tail) at least 2× the body", "Upper wick under half the body", "Any colour"],
		where: "After a drop, at support or in the EMA zone of an uptrend. In the middle of a range it means little.",
		trigger: true,
		textbook: 0.62,
		diagram: {
			type: "candles",
			bars: [[82, 84, 71, 73], [73, 75, 63, 65], [65, 67, 55, 57], [57, 59, 47, 49], [45, 49, 30, 48]],
			highlight: [4, 4],
			label: "hammer",
		},
	},
	{
		name: "Shooting Star",
		kind: "candle",
		bias: "bearish",
		size: "1 candle",
		says: "Buyers pushed price far up during the hour, but sellers slammed it back down before the close. The long spike on top is the rally that failed.",
		rules: ["Upper wick at least 2× the body", "Lower wick under half the body", "Any colour"],
		where: "After a rise, at resistance or in the EMA zone of a downtrend.",
		trigger: true,
		textbook: 0.6,
		diagram: {
			type: "candles",
			bars: [[18, 29, 16, 27], [27, 37, 25, 35], [35, 45, 33, 43], [43, 53, 41, 51], [55, 70, 51, 52]],
			highlight: [4, 4],
			label: "shooting star",
		},
	},
	{
		name: "Bullish Engulfing",
		kind: "candle",
		bias: "bullish",
		size: "2 candles",
		says: "A green candle whose body completely swallows the red body before it. Buyers took back everything sellers did last hour, and more.",
		rules: ["Previous candle red, this one green", "Opens below the previous close", "Closes above the previous open"],
		where: "At the end of a pullback in an uptrend, or at a clear floor.",
		trigger: true,
		textbook: 0.68,
		diagram: {
			type: "candles",
			bars: [[78, 80, 67, 69], [69, 71, 59, 61], [61, 63, 52, 54], [54, 56, 45, 47], [46, 60, 44, 58]],
			highlight: [3, 4],
			label: "engulfing",
		},
	},
	{
		name: "Bearish Engulfing",
		kind: "candle",
		bias: "bearish",
		size: "2 candles",
		says: "A red candle whose body completely swallows the green body before it. Sellers erased the last hour of buying, and more.",
		rules: ["Previous candle green, this one red", "Opens above the previous close", "Closes below the previous open"],
		where: "At the end of a bounce in a downtrend, or at a clear ceiling.",
		trigger: true,
		textbook: 0.66,
		diagram: {
			type: "candles",
			bars: [[22, 33, 20, 31], [31, 41, 29, 39], [39, 48, 37, 46], [46, 55, 44, 53], [54, 56, 40, 42]],
			highlight: [3, 4],
			label: "engulfing",
		},
	},
	{
		name: "Doji",
		kind: "candle",
		bias: "neutral",
		size: "1 candle",
		says: "Open and close are almost the same: buyers and sellers fought to a draw. It means \"undecided\", not up or down.",
		rules: ["Body smaller than 10% of the candle's full range"],
		where: "Only interesting at a level after a strong move. Wait for the next candle to pick a side.",
		textbook: 0.5,
		diagram: {
			type: "candles",
			bars: [[30, 42, 28, 40], [40, 50, 38, 48], [48, 57, 46, 55], [55.2, 66, 46, 55.6], [55.5, 58, 45, 47]],
			highlight: [3, 3],
			label: "doji",
		},
	},
	{
		name: "Three White Soldiers",
		kind: "candle",
		bias: "bullish",
		size: "3 candles",
		says: "Three strong green candles in a row, each closing higher with small wicks. Buyers are in steady control.",
		rules: ["Three green candles", "Each closes above the one before", "Each body is more than 70% of its range"],
		where: "Often late: after three big candles price is stretched, and buying here is how beginners buy the top.",
		textbook: 0.75,
		diagram: {
			type: "candles",
			bars: [[52, 54, 41, 43], [43, 46, 37, 41], [41, 50.5, 40.5, 50], [49.5, 60.5, 49, 60], [59.5, 71, 59, 70.5]],
			highlight: [2, 4],
			label: "3 soldiers",
		},
	},
	{
		name: "Three Black Crows",
		kind: "candle",
		bias: "bearish",
		size: "3 candles",
		says: "Three strong red candles in a row, each closing lower with small wicks. Sellers are in steady control.",
		rules: ["Three red candles", "Each closes below the one before", "Each body is more than 70% of its range"],
		where: "Often late: after three big drops price is stretched, and selling here is how beginners sell the bottom.",
		textbook: 0.73,
		diagram: {
			type: "candles",
			bars: [[48, 61, 46, 59], [59, 63, 54, 57], [57, 57.5, 47, 48], [48.5, 49, 38, 38.5], [39, 39.5, 28.5, 29]],
			highlight: [2, 4],
			label: "3 crows",
		},
	},
	{
		name: "Double Bottom",
		kind: "chart",
		bias: "bullish",
		size: "many candles",
		says: "Price drops to a floor, bounces, drops to the same floor again and holds. Sellers failed twice. It only counts once price closes above the bounce high (the neckline).",
		rules: ["Two swing lows within 2% of each other", "Neckline = highest point between them", "Confirmed only on a close above the neckline"],
		where: "After a downtrend. The classic plan: stop 2% under the lows, target = neckline + the pattern's height.",
		textbook: 0.71,
		diagram: {
			type: "line",
			points: [88, 74, 60, 46, 32, 44, 58, 49, 39, 31.5, 44, 62, 70, 78],
			neckline: 58,
			breakAt: 11,
			target: 85,
			stop: 26,
			marks: [{ i: 4, label: "1st low" }, { i: 9, label: "2nd low" }],
		},
	},
	{
		name: "Double Top",
		kind: "chart",
		bias: "bearish",
		size: "many candles",
		says: "Price rises to a ceiling, dips, rises to the same ceiling again and fails. Buyers failed twice. It only counts once price closes below the dip low (the neckline).",
		rules: ["Two swing highs within 2% of each other", "Neckline = lowest point between them", "Confirmed only on a close below the neckline"],
		where: "After an uptrend. The classic plan: stop 2% above the highs, target = neckline − the pattern's height.",
		textbook: 0.69,
		diagram: {
			type: "line",
			points: [12, 26, 40, 54, 68, 56, 42, 51, 61, 68.5, 56, 38, 30, 22],
			neckline: 42,
			breakAt: 11,
			target: 15,
			stop: 74,
			marks: [{ i: 4, label: "1st high" }, { i: 9, label: "2nd high" }],
		},
	},
	{
		name: "Head and Shoulders",
		kind: "chart",
		bias: "bearish",
		size: "many candles",
		says: "Three peaks, the middle one highest. The last rally (right shoulder) can't reach the head: buyers are tiring. It only counts once price closes below the neckline.",
		rules: ["Three swing highs, the middle one highest", "Outer two (shoulders) within 3% of each other", "Confirmed only on a close below the neckline (lowest dip between the peaks)"],
		where: "After an uptrend. Classic plan: stop 2% above the head, target = neckline − (head − neckline). The engine only looks for this bearish version, not the upside-down one.",
		textbook: 0.65,
		diagram: {
			type: "line",
			points: [18, 32, 52, 41, 58, 74, 57, 42, 52, 36, 26, 18],
			neckline: 41,
			breakAt: 9,
			target: 8,
			stop: 79,
			marks: [{ i: 2, label: "shoulder" }, { i: 5, label: "head" }, { i: 8, label: "shoulder" }],
		},
	},
];

// ─── Measured ───────────────────────────────────────────────────────────────
// backend/backtest-results/patterns-2026-10-05T02-14-17.json
// `bun src/backtest/patterns/run.ts --days 180` (backend/src/backtest/patterns).
// "Went its way": after the pattern's candle closed, price moved 1 ATR in the
// pattern's direction before 1 ATR against it, within 24h (5m candles).
// "Traded alone": next-open entry, stop 1.5 ATR, half off at 1.5R + stop to
// break-even, rest at 2.5R, 24h time stop — net of fees, slippage, funding.

export interface Measured {
	seen: number;
	/** Share that went the pattern's way (null = no direction, not tested). */
	wentItsWay: number | null;
	/** Average R per trade, net of costs (null = not traded). */
	netR: number | null;
	note?: string;
}

export const STUDY_INFO = {
	coins: 10,
	days: 180,
	window: "8 Apr – 5 Oct 2026",
	candles: 42_960,
	/** Any closed 1h candle, no pattern: went up first / a random long or short. */
	anyCandleWay: 0.51,
	anyLongR: -0.11,
	anyShortR: -0.21,
	rerun: "bun src/backtest/patterns/run.ts",
} as const;

export const MEASURED: Record<string, Measured> = {
	Hammer: { seen: 1196, wentItsWay: 0.5, netR: -0.15 },
	"Shooting Star": { seen: 1017, wentItsWay: 0.48, netR: -0.25 },
	"Bullish Engulfing": { seen: 1071, wentItsWay: 0.5, netR: -0.12 },
	"Bearish Engulfing": { seen: 1437, wentItsWay: 0.49, netR: -0.24 },
	Doji: { seen: 4721, wentItsWay: null, netR: null, note: "About 1 candle in 9 is a doji. It points nowhere, so there is nothing to test as a trade." },
	"Three White Soldiers": {
		seen: 30, wentItsWay: 0.3, netR: -0.47,
		note: "Rare, and price fell back more often than it kept going: by the third candle the move was usually over.",
	},
	"Three Black Crows": { seen: 10, wentItsWay: 0.6, netR: -0.21, note: "Only 10 seen in 180 days — too few to judge either way." },
	"Double Bottom": { seen: 931, wentItsWay: 0.47, netR: -0.18 },
	"Double Top": { seen: 794, wentItsWay: 0.54, netR: -0.1, note: "The best of the ten, and still no better than a coin flip once you allow for luck (the 95% range reaches 50%)." },
	"Head and Shoulders": { seen: 196, wentItsWay: 0.49, netR: -0.14 },
};
